interface MergeableJobLineItem {
  _rowId?: string;
  lineItemId?: string;
  configurationName?: string;
  code?: string;
  type?: string;
  name?: string;
  provenance?: {
    source: 'ESTIMATE' | 'JOB_TYPE';
  };
}

export type EstimateLineChangeAction = 'edit' | 'remove';

export function estimateLineChangeKey(
  item: Pick<MergeableJobLineItem, '_rowId'>,
  action: EstimateLineChangeAction,
): string {
  return `${item._rowId ?? 'unknown'}:${action}`;
}

/**
 * Estimate-origin rows get one explicit acknowledgement per action. Once the
 * dispatcher accepts editing a row, normal field editing remains fluid; remove
 * still receives its own warning because it is a separate destructive action.
 */
export function shouldConfirmEstimateLineChange(
  item: MergeableJobLineItem | undefined,
  action: EstimateLineChangeAction,
  approvedChanges: ReadonlySet<string>,
): boolean {
  return Boolean(
    item?.provenance?.source === 'ESTIMATE' &&
    !approvedChanges.has(estimateLineChangeKey(item, action)),
  );
}

function normalizeIdentityPart(value: string | undefined): string {
  return (value ?? '').trim().toLocaleLowerCase('en-US');
}

function lineItemIdentity(item: MergeableJobLineItem): string {
  const configuration = normalizeIdentityPart(item.configurationName);
  if (item.lineItemId?.trim()) {
    return `catalog:${item.lineItemId.trim()}:${configuration}`;
  }

  return [
    'custom',
    normalizeIdentityPart(item.type),
    normalizeIdentityPart(item.code),
    normalizeIdentityPart(item.name),
    configuration,
  ].join(':');
}

/**
 * Keeps every existing row and appends only template rows that are not already
 * represented by the same catalog/configuration (or custom-line identity).
 */
export function mergeJobLineItems<T extends MergeableJobLineItem>(
  existing: readonly T[],
  incoming: readonly T[],
): T[] {
  const merged = [...existing];
  const identities = new Set(existing.map(lineItemIdentity));

  for (const item of incoming) {
    const identity = lineItemIdentity(item);
    if (identities.has(identity)) continue;
    identities.add(identity);
    merged.push(item);
  }

  return merged;
}
